import { GENDER } from '../../domain/domainCodes.js'
import { useEffect, useMemo, useState } from 'react'
import { Eye, EyeOff, Lock, Pencil, ShieldCheck, TriangleAlert, UserCog } from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import { FormBody, FormCols, FormWell, InfoNote } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Drawer from '../../components/Drawer'
import Select from '../../components/Select'
import Tabs from '../../components/Tabs'
import TextField from '../../components/TextField'
import { LoadingState } from '../../components/States'
import { getUserRoles } from './usersService'
import { strings as S } from './strings'
import './modals.css'

const TABS = [
  { id: 'profile', label: S.edit.tabs.profile, icon: Pencil },
  { id: 'permissions', label: S.edit.tabs.permissions, icon: ShieldCheck },
  { id: 'password', label: S.edit.tabs.password, icon: Lock },
]

const GENDERS = [
  { value: String(GENDER.MALE), label: S.edit.male },
  { value: String(GENDER.FEMALE), label: S.edit.female },
]

/**
 * `#edit_modal` — the three-tab profile editor opened by the row's pencil.
 *
 * The legacy modal has no footer; each tab carries its own submit. `isLocal` is the
 * signed-in admin's own login provider, not the edited user's, which is what gates
 * the password tab (`BaseController.cs:62`).
 */
export default function UserEditModal({
  open,
  user,
  groups,
  /** `{ id, name }` from the page payload — `id` is the role Identity stores. */
  roles = [],
  isLocal = true,
  onClose,
  onSubmit,
}) {
  const [tab, setTab] = useState('profile')
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [grantedRoles, setGrantedRoles] = useState([])
  const [rolesLoaded, setRolesLoaded] = useState(false)
  const [passwords, setPasswords] = useState({ next: '', confirm: '' })
  const [reveal, setReveal] = useState({ next: false, confirm: false })

  /* `setForm(User)` fills every field from the row and clicks back to the first tab.
     The row is the only source there is — see `toRow` in usersService.

     `nameAr` reads `user.nameAr`, NOT `user.name`: the latter is whichever
     language the list is running in, so an English panel would have written the
     English name into the Arabic column on the first save.

     `attributes` is not shown or edited here; it is held so the save can send it
     back unchanged. The handler nulls every column it is not given. */
  useEffect(() => {
    if (!open || !user) return
    setTab('profile')
    setErrors({})
    setPasswords({ next: '', confirm: '' })
    setValues({
      nameAr: user.nameAr ?? '',
      nameEn: user.nameEn ?? '',
      group: user.groupId == null ? '' : String(user.groupId),
      gender: user.gender == null ? '' : String(user.gender),
      email: user.email ?? '',
      phone: user.phone ?? '',
      mobile: user.mobile ?? '',
      employeeId: user.employeeId ?? '',
      attributes: user.attributes ?? [],
    })
    setGrantedRoles([])
    setRolesLoaded(false)
  }, [open, user])

  /* The roles are NOT on the row — they live in Identity, and `/Users/GetUserRole` is
     the only thing that answers them. Without this the permissions tab opened with
     every box clear, and "تحديث الصلاحيات" then posted that empty list: one press
     stripped an administrator of everything they had.

     `rolesLoaded` is what keeps that impossible. Until the answer is in, the tab
     shows a loader instead of a list of unticked boxes — an unanswered question and
     "this user has no roles" must never look the same. */
  useEffect(() => {
    if (!open || !user?.userName) return undefined

    const controller = new AbortController()
    getUserRoles(user.userName, controller.signal)
      .then((granted) => {
        setGrantedRoles(granted)
        setRolesLoaded(true)
      })
      .catch(() => {
        /* Aborted or refused — leave the tab in its loading state rather than
           presenting an empty list as this user's roles. */
      })

    return () => controller.abort()
  }, [open, user?.userName])

  /* A company admin never sees SystemManagers (UsersController.cs:136-150). The
     server already filters its own list; this is the second half of the same rule
     and costs nothing if the list arrives without it. */
  const visibleRoles = useMemo(() => roles.filter((role) => role.id !== 'SystemManagers'), [roles])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const submitProfile = (event) => {
    event.preventDefault()
    const next = {}
    if (!values.nameAr?.trim()) next.nameAr = S.edit.required
    else if (values.nameAr.trim().length < 3 || values.nameAr.length > 200)
      next.nameAr = S.edit.lengthError
    if (!values.nameEn?.trim()) next.nameEn = S.edit.required
    else if (values.nameEn.trim().length < 3 || values.nameEn.length > 200)
      next.nameEn = S.edit.lengthError
    if (!values.group) next.group = S.edit.required
    if (!values.email?.trim()) next.email = S.create.errors.emailRequired
    setErrors(next)
    if (Object.keys(next).length === 0) onSubmit('profile', values)
  }

  const toggleRole = (name) =>
    setGrantedRoles((previous) =>
      previous.includes(name) ? previous.filter((item) => item !== name) : [...previous, name],
    )

  const allGranted =
    visibleRoles.length > 0 && visibleRoles.every((role) => grantedRoles.includes(role.id))

  const passwordField = (key, label) => (
    <FormRow label={label}>
      {(id) => (
        <TextField
          id={id}
          type={reveal[key] ? 'text' : 'password'}
          value={passwords[key]}
          onChange={(value) => setPasswords((previous) => ({ ...previous, [key]: value }))}
          autoComplete="new-password"
          trailing={
            <button
              type="button"
              onClick={() => setReveal((previous) => ({ ...previous, [key]: !previous[key] }))}
              aria-label={label}
            >
              {reveal[key] ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />
      )}
    </FormRow>
  )

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={user?.name ?? S.edit.title}
      icon={UserCog}
      subtitle={user ? `${user.userName} — ${user.email}` : undefined}
      onClose={onClose}
    >
      <div className="user-modal__split">
        <Tabs items={TABS} value={tab} onChange={setTab} tone="light" ariaLabel={S.edit.title} />

        <div className="user-modal__pane">
          {tab === 'profile' && (
            <form onSubmit={submitProfile} noValidate>
              {/* Eight fields, each of them short — a phone number does not need
                  the whole pane. Two up, so the tab is read rather than
                  scrolled. */}
              <FormCols>
                <FormRow label={S.edit.nameAr} error={errors.nameAr}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={values.nameAr ?? ''}
                      onChange={set('nameAr')}
                      invalid={!!errors.nameAr}
                    />
                  )}
                </FormRow>

                <FormRow label={S.edit.nameEn} error={errors.nameEn}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.nameEn ?? ''}
                      onChange={set('nameEn')}
                      invalid={!!errors.nameEn}
                    />
                  )}
                </FormRow>

                <FormRow label={S.edit.group} error={errors.group}>
                  {(id) => (
                    <Select
                      id={id}
                      value={values.group ?? ''}
                      onChange={set('group')}
                      options={groups}
                      placeholder=""
                    />
                  )}
                </FormRow>

                <FormRow label={S.edit.gender}>
                  {(id) => (
                    <Select
                      id={id}
                      value={values.gender ?? ''}
                      onChange={set('gender')}
                      options={GENDERS}
                      placeholder={S.edit.selectGender}
                    />
                  )}
                </FormRow>

                <FormRow className="form-span" label={S.edit.email} error={errors.email}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.email ?? ''}
                      onChange={set('email')}
                      invalid={!!errors.email}
                    />
                  )}
                </FormRow>

                <FormRow label={S.edit.phone}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.phone ?? ''}
                      onChange={set('phone')}
                    />
                  )}
                </FormRow>

                <FormRow label={S.edit.mobile}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.mobile ?? ''}
                      onChange={set('mobile')}
                    />
                  )}
                </FormRow>

                <FormRow label={S.edit.employeeId}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={values.employeeId ?? ''}
                      onChange={set('employeeId')}
                    />
                  )}
                </FormRow>
              </FormCols>

              <div className="user-modal__actions">
                <Button type="submit">{S.edit.save}</Button>
              </div>
            </form>
          )}

          {tab === 'permissions' &&
            /* Nothing is offered until the current grant is known — this form
               REPLACES the role set, so acting on an unloaded one revokes. */
            (!rolesLoaded ? (
              <LoadingState inline />
            ) : (
              <div className="user-modal__roles">
                <div className="user-modal__roles-head">
                  <Checkbox
                    id="roles-all"
                    checked={allGranted}
                    onChange={() =>
                      setGrantedRoles(allGranted ? [] : visibleRoles.map((role) => role.id))
                    }
                  >
                    <bdi>{S.edit.selectAllRoles}</bdi>
                  </Checkbox>
                  <Button onClick={() => onSubmit('roles', grantedRoles)}>
                    {S.edit.updateRoles}
                  </Button>
                </div>

                <ul className="user-modal__role-list">
                  {visibleRoles.map((role) => (
                    <li key={role.id} className="user-modal__role">
                      <Checkbox
                        id={`role-${role.id}`}
                        checked={grantedRoles.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                      >
                        <bdi>{role.name}</bdi>
                      </Checkbox>
                      <span className="user-modal__role-kind">
                        <bdi>{role.id === 'Users' ? S.edit.roleUser : S.edit.roleAdmin}</bdi>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

          {tab === 'password' &&
            (isLocal ? (
              <FormBody>
                {/* Two different ways to do the same thing — send one, or set
                    one. They used to run together as paragraph, button, rule,
                    paragraph, fields, button, so which button belonged to
                    which explanation had to be read out of the spacing. A well
                    each makes the pair a choice. */}
                <FormWell>
                  <p className="user-modal__note">
                    <bdi>{S.edit.autoPasswordHint}</bdi>
                  </p>
                  <div className="user-modal__actions user-modal__actions--start">
                    <Button variant="soft" onClick={() => onSubmit('resetPassword')}>
                      {S.edit.autoPasswordButton}
                    </Button>
                  </div>
                </FormWell>

                <FormWell>
                  <p className="user-modal__note">
                    <bdi>{S.edit.manualPasswordHint}</bdi>
                  </p>

                  <FormCols>
                    {passwordField('next', S.edit.newPassword)}
                    {passwordField('confirm', S.edit.confirmNewPassword)}
                  </FormCols>

                  <div className="user-modal__actions user-modal__actions--start">
                    <Button
                      onClick={() =>
                        onSubmit(
                          passwords.next && passwords.next === passwords.confirm
                            ? 'changePassword'
                            : 'passwordMismatch',
                          passwords,
                        )
                      }
                    >
                      {S.edit.changePassword}
                    </Button>
                  </div>
                </FormWell>
              </FormBody>
            ) : (
              <InfoNote icon={TriangleAlert} lead={S.edit.noPasswordChange}>
                <bdi>{S.edit.noPasswordChangeReason}</bdi>
              </InfoNote>
            ))}
        </div>
      </div>
    </Drawer>
  )
}
